import { BaseQueryFn } from "@reduxjs/toolkit/query"
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import { isSelfAuthenticatingUrl } from "@/api/authEndpoints"
import { isTransientRefreshFailure } from "@/api/refreshFailure"
import { isTokenExpired } from "@/helpers/jwt"
import { captureException } from "@/helpers/sentry"

const getApiBaseUrl = () => {
  return import.meta.env.VITE_BACKEND
}

const getAccessToken = (): string | null => {
  const token = sessionStorage.getItem("accessToken")
  if (token) {
    return token
  }

  const oldToken = localStorage.getItem("accessToken")
  if (oldToken) {
    sessionStorage.setItem("accessToken", oldToken)
    localStorage.removeItem("accessToken")
    return oldToken
  }

  return null
}

const getRefreshToken = (): string | null => {
  return localStorage.getItem("refreshToken")
}

export const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: false
})

axiosInstance.interceptors.request.use(
  (config) => {
    if (
      (config as InternalAxiosRequestConfig & { skipAuthRefresh?: boolean })
        .skipAuthRefresh
    ) {
      return config
    }

    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    if (refreshToken) {
      config.headers["x-refresh-token"] = refreshToken
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

type IAppStore = {
  dispatch: (action: unknown) => void
}
type ILogoutAction = (value: boolean) => { type: string; payload: boolean }

let appStore: IAppStore | null = null
let logoutAction: ILogoutAction | null = null

export const injectStore = (store: IAppStore, action: ILogoutAction) => {
  appStore = store
  logoutAction = action
}

const accessTokenSetListeners = new Set<(accessToken: string) => void>()

export const subscribeAccessTokenSet = (
  listener: (accessToken: string) => void
): (() => void) => {
  accessTokenSetListeners.add(listener)
  return () => {
    accessTokenSetListeners.delete(listener)
  }
}

const notifyAccessTokenSet = (accessToken: string) => {
  accessTokenSetListeners.forEach((listener) => {
    try {
      listener(accessToken)
    } catch (error) {
      captureException(error, { context: "Access token set listener failed" })
    }
  })
}

export const setAuthHeader = (accessToken: string, refreshToken: string) => {
  sessionStorage.setItem("accessToken", accessToken)
  localStorage.setItem("refreshToken", refreshToken)

  axiosInstance.defaults.headers["Authorization"] = `Bearer ${accessToken}`
  axiosInstance.defaults.headers["x-refresh-token"] = refreshToken

  notifyAccessTokenSet(accessToken)
}

export const clearAuthHeaders = () => {
  sessionStorage.removeItem("accessToken")
  localStorage.removeItem("accessToken")
  localStorage.removeItem("refreshToken")
  delete axiosInstance.defaults.headers["Authorization"]
  delete axiosInstance.defaults.headers["x-refresh-token"]
}

const handleAuthError = () => {
  clearAuthHeaders()
  if (appStore && logoutAction) {
    appStore.dispatch(logoutAction(false))
  }
}

let refreshPromise: Promise<string> | null = null
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve()
    }
  })
  failedQueue = []
}

const refreshTokens = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        throw new Error("refresh: no refresh token in storage")
      }
      const { data } = await axiosInstance.post<{
        accessToken: string
        refreshToken: string
      }>("/auth/refresh", undefined, {
        skipAuthRefresh: true,
        headers: { Authorization: `Bearer ${refreshToken}` }
      } as InternalAxiosRequestConfig & { skipAuthRefresh?: boolean })
      setAuthHeader(data.accessToken, data.refreshToken)
      processQueue(null)
      return data.accessToken
    } catch (error) {
      if (!isTransientRefreshFailure(error)) {
        handleAuthError()
      }
      processQueue(error)
      throw error
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export const refreshTokensIfNeeded = async (): Promise<boolean> => {
  const accessToken = getAccessToken()
  if (accessToken && !isTokenExpired(accessToken)) {
    return false
  }

  const refreshToken = getRefreshToken()
  if (!refreshToken || isTokenExpired(refreshToken)) {
    return false
  }

  try {
    await refreshTokens()
    return true
  } catch {
    return false
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      skipAuthRefresh?: boolean
    }

    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error)
    }

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isSelfAuthenticatingUrl(originalRequest?.url)
    ) {
      if (refreshPromise) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            const accessToken = getAccessToken()
            if (accessToken && originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
            return axiosInstance(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true

      try {
        await refreshTokens()
        const accessToken = getAccessToken()
        if (accessToken && originalRequest) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return axiosInstance(originalRequest)
      } catch (refreshError) {
        captureException(refreshError, {
          context: "Token refresh failed"
        })
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

interface IAxiosBaseQueryArgs {
  url: string
  headers?: Record<string, string>
  method: string
  data?: unknown
  params?: unknown
}

export const axiosBaseQuery =
  (): BaseQueryFn<IAxiosBaseQueryArgs, unknown, unknown> =>
  async ({ url, headers, method, data, params }) => {
    const isFormData = data instanceof FormData

    try {
      const result = await axiosInstance({
        url,
        headers: {
          "Content-Type": isFormData
            ? "multipart/form-data"
            : "application/json",
          ...headers
        },
        method,
        data,
        params
      })
      return { data: result?.data }
    } catch (error: unknown) {
      const axiosError = error as AxiosError

      const status = axiosError.response?.status
      captureException(error, {
        url,
        method,
        status,
        responseData: axiosError.response?.data
      })

      return { error }
    }
  }
